/**
 * Générateur — Il se repère sur une sphère (latitude, longitude).
 * Attendu ID : 238 | Niveau : 3ème | Domaine : Espace (d20, O1)
 *
 * Source doc prof (ESPACE ET GEOMETRIE 3e, séance D5S1 + Créé n°16). Plan
 * auto-validé 2026-07-18 (TODO §6) : « hemispheres » (Paris/Sydney : QCM
 * hémisphère / proche de l'équateur), « villes » (V/F calculés sur les
 * coordonnées), « arc » (le mile nautique : méridien, arc de 1°, écart de
 * latitudes d'hémisphères OPPOSÉS — le piège du doc), « croisees » (F prend
 * la longitude de B et la latitude de C), « sphere » (diamètre et grand
 * cercle). Adaptation : les placements sur globe (interactifs) sont portés
 * par la lecture/l'écriture de coordonnées (pas d'interactif sphère).
 */

import { randomChoice, randInt, shuffle, tex, makeQcm, makeTexteTrou, makeVraiFaux } from '../../../utils';

const ATTENDU_ID = 238;
const L_HEMI = 'Hémisphères et distance à l\'équateur';
const L_VILLES = 'Vrai ou faux sur des coordonnées de villes';
const L_ARC = 'Longueurs d\'arcs sur un méridien';
const L_CROISEES = 'Coordonnées croisées (même latitude, même longitude)';
const L_SPHERE = 'Diamètre et grand cercle d\'une sphère';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Villes réelles (coordonnées arrondies) : [nom, lat, N/S, long, E/O]
const VILLES = [
    ['Paris', 49, 'N', 2, 'E'], ['Sydney', 34, 'S', 151, 'E'], ['Kiruna', 68, 'N', 20, 'E'],
    ['Durban', 30, 'S', 31, 'E'], ['Brasilia', 16, 'S', 47, 'O'], ['Athènes', 38, 'N', 24, 'E'],
    ['Mexico', 20, 'N', 100, 'O'], ['Vancouver', 49, 'N', 123, 'O'], ['Le Caire', 30, 'N', 31, 'E'],
    ['Shanghai', 31, 'N', 121, 'E'], ['Buenos Aires', 34, 'S', 58, 'O'], ['Douala', 4, 'N', 10, 'E'],
];

const deuxVilles = () => {
    let v1; let v2;
    for (let essai = 0; essai < 40; essai++) {
        [v1, v2] = shuffle(VILLES).slice(0, 2);
        if (v1[1] !== v2[1]) break;                        // latitudes distinctes → « plus proche » décidable
    }
    return [v1, v2];
};

const affCoord = (v) => `(${v[1]}° ${v[2]} ; ${v[3]}° ${v[4]})`;

// ═════════ « hemispheres » ═════════

const genHemiQcm = () => {
    const [v1, v2] = deuxVilles();
    if (randInt(0, 1)) {
        const cible = randomChoice([v1, v2]);
        const correct = cible[2] === 'N' ? 'Dans l\'hémisphère Nord' : 'Dans l\'hémisphère Sud';
        const payload = makeQcm(['Dans l\'hémisphère Nord', 'Dans l\'hémisphère Sud', 'Sur l\'équateur'], correct,
            `<p>${v1[0]} a pour coordonnées ${affCoord(v1)} et ${v2[0]} ${affCoord(v2)}.</p>`
            + `<p><b>Dans quel hémisphère se trouve ${cible[0]} ?</b> (La lettre N ou S de la LATITUDE le dit.)</p>`,
            opts(L_HEMI));
        payload._meta.params = { v1: v1[0], v2: v2[0], cible: cible[0] };
        return payload;
    }
    const proche = v1[1] < v2[1] ? v1 : v2;
    const payload = makeQcm([v1[0], v2[0], 'Elles sont à égale distance'], proche[0],
        `<p>${v1[0]} a pour coordonnées ${affCoord(v1)} et ${v2[0]} ${affCoord(v2)}.</p>`
        // « Compare les latitudes, peu importe N ou S » disait à la fois quelle
        // coordonnée regarder et comment traiter l'hémisphère.
        + '<p><b>Laquelle est la plus proche de l\'équateur ?</b></p>',
        opts(L_HEMI));
    payload._meta.params = { v1: v1[0], v2: v2[0], proche: proche[0] };
    return payload;
};

// ═════════ « villes » — V/F calculés ═════════

const genVillesVf = () => {
    const [v1, v2] = deuxVilles();
    const lignes = []; const bonnes = [];
    // 1. même méridien ?
    const memeLong = v1[3] === v2[3] && v1[4] === v2[4];
    lignes.push(`${v1[0]} et ${v2[0]} sont sur le même méridien.`);
    bonnes.push(memeLong ? 0 : 1);
    // 2. hémisphère
    if (randInt(0, 1)) { lignes.push(`${v1[0]} est dans l'hémisphère ${v1[2] === 'N' ? 'Nord' : 'Sud'}.`); bonnes.push(0); }
    else { lignes.push(`${v1[0]} est dans l'hémisphère ${v1[2] === 'N' ? 'Sud' : 'Nord'}.`); bonnes.push(1); }
    // 3. plus proche de l'équateur
    const v1Proche = v1[1] < v2[1];
    if (randInt(0, 1)) { lignes.push(`${v1[0]} est plus proche de l'équateur que ${v2[0]}.`); bonnes.push(v1Proche ? 0 : 1); }
    else { lignes.push(`${v2[0]} est plus proche de l'équateur que ${v1[0]}.`); bonnes.push(v1Proche ? 1 : 0); }
    // 4. latitude Est/Ouest (confusion d'axes)
    lignes.push(`La latitude de ${v2[0]} est ${v2[1]}° ${v2[2]}.`);
    bonnes.push(0);
    if (!bonnes.includes(1)) { lignes[3] = `La latitude de ${v2[0]} est ${v2[3]}° ${v2[4]}.`; bonnes[3] = 1; }
    const ordre = shuffle(lignes.map((_, i) => i));
    const payload = makeVraiFaux(ordre.map(i => lignes[i]), ordre.map(i => bonnes[i]),
        `<p>${v1[0]} : ${affCoord(v1)} ; ${v2[0]} : ${affCoord(v2)}.</p><p><b>Vrai ou faux ?</b></p>`,
        opts(L_VILLES));
    payload._meta.params = { v1: v1[0], v2: v2[0], bonnes };
    return payload;
};

// ═════════ « arc » — le mile nautique généralisé ═════════

const genArcTrous = () => {
    // Planète de rayon R : demi-méridien = πR ; arc de 1° = πR/180 ; villes à
    // lat1 N et lat2 S sur le MÊME méridien → écart = lat1 + lat2 (le piège).
    let R; let demi; let arc10;
    for (let essai = 0; essai < 60; essai++) {
        R = randomChoice([1800, 2700, 3600, 5400, 6370]);
        demi = Math.round(Math.PI * R);
        arc10 = Math.round((Math.PI * R * 10) / 180);      // arc de 1° en DIXIÈMES
        const f1 = Math.PI * R; const f2 = (Math.PI * R * 10) / 180;
        if (Math.abs(f1 - Math.floor(f1) - 0.5) > 0.05 && Math.abs(f2 - Math.floor(f2) - 0.5) > 0.05) break;
        demi = null;
    }
    if (demi === null) { R = 6370; demi = 20012; arc10 = 1112; }
    const lat1 = randInt(20, 60); const lat2 = randInt(15, 50);
    const arcAff = `${Math.floor(arc10 / 10)},${arc10 % 10}`;
    const phrase = `$\\text{Demi-méridien} \\approx {{${demi}}}\\ \\text{km} \\qquad`
        + ` \\text{arc de 1°} \\approx {{${arcAff}}}\\ \\text{km} \\qquad`
        + ` \\text{écart de latitudes : } {{${lat1 + lat2}}}°$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une planète a un rayon de ${R} km. Un méridien complet est un grand cercle ; le DEMI-méridien `
        + 'va d\'un pôle à l\'autre. Deux villes sont sur le même méridien, l\'une à '
        + `${lat1}° NORD, l'autre à ${lat2}° SUD.</p>`
        // Le piège central (hémisphères opposés → les latitudes s'AJOUTENT)
        // n'est PLUS divulgué : sinon l'erreur type visée (soustraire) est
        // impossible et l'élève ne fait qu'exécuter la consigne.
        // Les deux formules (π × R, ÷ 180) étaient les calculs à trouver ; seule
        // la précision d'arrondi reste, en phrase, car elle est imposée (§0.1).
        + '<p><b>Calcule la longueur du demi-méridien, celle d\'un arc de 1°, puis l\'écart de '
        + 'latitudes entre les deux villes.</b> Arrondis le demi-méridien au km et l\'arc au dixième.</p>',
        { ...opts(L_ARC), mode: 'saisie' });
    payload._meta.params = { R, demi, arc10, lat1, lat2 };
    return payload;
};

// ═════════ « croisees » — F(longitude de B ; latitude de C) ═════════

const genCroiseesTrous = () => {
    const [b, c] = deuxVilles();
    const phrase = `F : ({{${c[1]}}}° ${c[2]} ; {{${b[3]}}}° ${b[4]}).`;
    const payload = makeTexteTrou(phrase,
        `<p>Sur un globe, B = ${b[0]} a pour coordonnées ${affCoord(b)} et C = ${c[0]} ${affCoord(c)}. `
        + 'On place un point F qui a la LATITUDE de C et la LONGITUDE de B.</p>'
        // L'ordre latitude puis longitude est une contrainte d'écriture (§0.1) :
        // elle reste, en phrase.
        + '<p><b>Donne les coordonnées de F</b>, la latitude d\'abord.</p>',
        { ...opts(L_CROISEES), mode: 'saisie' });
    payload._meta.params = { b: b[0], c: c[0] };
    return payload;
};

// ═════════ « sphere » — diamètre, grand cercle ═════════

const genSphereTrous = () => {
    const r = randInt(3, 15);
    const [d1, d2] = randomChoice([['A', 'D'], ['A', 'B'], ['M', 'N'], ['P', 'Q']]);
    const phrase = `$${d1}${d2} = {{${2 * r}}}\\ \\text{cm} \\qquad`
        + ` \\text{longueur d'un grand cercle : } {{${2 * r}}} \\pi\\ \\text{cm}$`;
    // Recontextualisé sur le REPÉRAGE (équateur / méridiens / axe des pôles) :
    // l'item ne parlait que de diamètre et de grand cercle, hors de l'attendu
    // « se repérer sur une sphère ».
    const payload = makeTexteTrou(phrase,
        `<p>Un globe a pour centre O et pour rayon ${r} cm. Sur ce globe, l'ÉQUATEUR et les MÉRIDIENS, `
        + 'les cercles qui servent à repérer latitude et longitude, sont des grands cercles : '
        + `des cercles de centre O tracés sur la sphère, donc de rayon ${r} cm. `
        + `${tex(`\\lbrack ${d1}${d2}\\rbrack`)} est le diamètre qui joint les deux pôles.</p>`
        + `<p><b>Donne la longueur de l'axe des pôles ${tex(`\\lbrack ${d1}${d2}\\rbrack`)}, puis celle `
        + 'de l\'équateur.</b> Pour l\'équateur, complète seulement le coefficient de π.</p>',
        { ...opts(L_SPHERE), mode: 'saisie' });
    payload._meta.params = { r };
    return payload;
};

const GENERATORS = {
    hemispheres_qcm_unique: genHemiQcm,
    villes_tableau_vf: genVillesVf,
    arc_texte_trou_saisie: genArcTrous,
    croisees_texte_trou_saisie: genCroiseesTrous,
    sphere_texte_trou_saisie: genSphereTrous,
};
const FORMAT_LABELS = { qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte_trou_saisie: 'Saisie trous' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'hemispheres', label: L_HEMI, formatsList: buildFormats('hemispheres') },
    { id: 'villes', label: L_VILLES, formatsList: buildFormats('villes') },
    { id: 'arc', label: L_ARC, formatsList: buildFormats('arc') },
    { id: 'croisees', label: L_CROISEES, formatsList: buildFormats('croisees') },
    { id: 'sphere', label: L_SPHERE, formatsList: buildFormats('sphere') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
